import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableFooter from '@mui/material/TableFooter'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import tableCellClasses from '@mui/material/TableCell/tableCellClasses'
import TablePagination from '@mui/material/TablePagination'
import IconButton from '@mui/material/IconButton'
import TableSortLabel from '@mui/material/TableSortLabel'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import { cvProjectsAPI, cvEmployersAPI } from 'api/CvAPI'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import { orderBy } from 'lodash'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IProject, IEmployer, IPersonSkillOrIndustryOrRole } from 'types/cvsInterfaces'
import { IError } from 'types/error'
import { CvId } from 'types/ids'
import { getSkillExperienceLabel, toPositiveInteger } from 'utils/utils'
import { useNotificationPopup } from 'Components/reusable/Notification'
import DeleteConnectedSkillConfirm from '../DeleteConnectedSkillConfirm'
import SkillsItem from '../SkillsItem'
import { TableInputField } from 'Components/reusable/InputFields/TableInputField'
import CardContentText from 'Components/reusable/CaleoCustomComponents/CardContentText'
import Grid from '@mui/material/Grid2'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import { getSkillsInitialData } from 'Components/reusable/DataContext/InitialData'
import { chooseDBTranslation } from 'utils/translations'
import AddToArrayButton from 'Components/reusable/Buttons/AddToArrayButton'
import { Schema } from 'yup'
import SkillLevelChip from 'Components/reusable/CaleoCustomComponents/SkillLevelChip'
import AutoCalculatedInfo from 'Components/reusable/CaleoCustomComponents/AutoCalculatedInfo'
import {
  ColumnDef,
  PaginationState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  Row,
} from '@tanstack/react-table'
import TablePaginationActions from 'Components/reusable/Tables/CustomTable/TablePaginationActions'
import { useLocalStorage } from 'hooks/data'
import AdditionRow from './AdditionRow'
import EditRow from './EditRow'
import { EditableFields, ErrorFields } from 'types/skillsTableInterfaces'

export interface ISkillsApi {
  create: (cvId: number, data?: unknown) => Promise<IPersonSkillOrIndustryOrRole>
  save: (item: IPersonSkillOrIndustryOrRole, data?: unknown) => Promise<IPersonSkillOrIndustryOrRole>
  delete: (id: number) => Promise<void>
}

interface IComponentProps {
  editable: boolean
  setSkills: (newSkills: IPersonSkillOrIndustryOrRole[]) => void
  setProjects: (projects: IProject[]) => void
  setEmployers: (employers: IEmployer[]) => void
  type: string
  handleClose: (newItem: IPersonSkillOrIndustryOrRole) => void
  items: IPersonSkillOrIndustryOrRole[]
  api: ISkillsApi
  cvId: CvId
  schema: Schema<unknown>
  updateProgress: () => void
  userProfile: boolean
}

/**
 * Skills table.
 *
 * @param onOpen - Function to open row for editing.
 * @param editable - Whether skills are editable.
 * @param setSkills - Function to set skills.
 * @param setProjects - Function to set projects.
 * @param setEmployers - Function to set employers.
 * @param type - Type of skills.
 * @param handleClose - Function to close row.
 * @param api - API instance.
 * @param items - Skills.
 * @param cvId - CV ID.
 * @param schema - Validation schema.
 * @returns Table component for skills.
 * @notExported
 */
const SkillsTable: React.FC<IComponentProps> = ({
  editable,
  setSkills,
  setProjects,
  setEmployers,
  type,
  handleClose,
  api,
  items,
  cvId,
  schema,
  updateProgress,
  userProfile,
}) => {
  const { t, i18n } = useTranslation()
  const { setSuccessNotificationPopup } = useNotificationPopup()
  const tableRef = useRef<HTMLDivElement>(null)
  const scrollAfterPageChange = useRef(false)
  const pendingWarningItemId = useRef<number | string | undefined>(undefined)
  const [deleteConnectedSkillConfirm, setDeleteConnectedSkillConfirm] = useState<boolean>(false)
  const [editableSkill, setEditableSkill] = useState<IPersonSkillOrIndustryOrRole>()
  const [backendError, setBackendError] = useState<IError>()
  const [updateConnected, setUpdateConnected] = useState<boolean>(false)
  const [addNew, setAddNew] = useState<boolean>(false)
  const [errors, setErrors] = useState<ErrorFields<EditableFields<IPersonSkillOrIndustryOrRole>>>({})
  const [count, setCount] = useLocalStorage(`${type}Count`, 25)
  const [page, setPage] = useLocalStorage(`${type}Page`, 0)
  const [sorting, setSorting] = useState<SortingState>([])

  useEffect(() => {
    if (!userProfile) {
      setCount(25)
      setPage(0)
    }
  }, [setCount, setPage, userProfile])

  useEffect(() => {
    if (!scrollAfterPageChange.current) return
    scrollAfterPageChange.current = false
    const warningItemId = pendingWarningItemId.current
    pendingWarningItemId.current = undefined

    requestAnimationFrame(() => {
      const target = warningItemId
        ? document.getElementById(`profile-item-${getType(type)}-${warningItemId}`)
        : undefined

      if (target) {
        target.classList.remove('profile-warning-item-highlight')
        void target.offsetWidth
        target.classList.add('profile-warning-item-highlight')
      }
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      target?.focus({ preventScroll: true })
      if (!target) {
        tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    })
  }, [page, count, type])

  useEffect(() => {
    const controller = new AbortController()

    if (editableSkill && updateConnected === true) {
      ;(async () => {
        try {
          const projects = await cvProjectsAPI.getList(editableSkill.cvId, controller)
          const employers = await cvEmployersAPI.getList(editableSkill.cvId, controller)
          {
            setProjects(projects)
            setEmployers(employers)
            setUpdateConnected(false)
          }
        } catch (error) {
          setBackendError(error as IError)
        }
      })()
    }

    return () => {
      controller.abort()
    }
  }, [editableSkill, setEmployers, setProjects, updateConnected])

  useEffect(() => {
    setEditableSkill(undefined)
  }, [i18n.language])

  const getDisabledOptions = useMemo(() => {
    if (items) {
      return items.filter(item => item.Skill).map(personSkill => personSkill.Skill)
    }
    return []
  }, [items])

  const onSubmit = async (newSkill: IPersonSkillOrIndustryOrRole) => {
    if (items) {
      if (Object.keys(errors).length < 1) {
        try {
          let result: IPersonSkillOrIndustryOrRole
          if (editableSkill && editableSkill.id) {
            result = await api.save(newSkill)
          } else {
            result = await api.create(newSkill.cvId, newSkill)
          }

          {
            const newSkills: IPersonSkillOrIndustryOrRole[] = editableSkill?.id
              ? items.map(skill => {
                  if (skill.id === result.id || skill.skillId === result.skillId) {
                    return result
                  }
                  return skill
                })
              : items

            if (!editableSkill?.id) {
              newSkills.push(result)
            }

            setSkills(newSkills)
            setEditableSkill(undefined)
            setSuccessNotificationPopup()
            setErrors({})
            handleClose(result)
            setAddNew(false)
          }
        } catch (err) {
          setBackendError(err as IError)
        }
      }
    }
  }

  const deleteItem = async () => {
    for (const item of items) {
      if (item.id === editableSkill?.id) {
        const currentItem = item as IPersonSkillOrIndustryOrRole
        try {
          await api.delete(currentItem.id)
          {
            setSkills(items.filter(item => item.id !== currentItem.id))
            setUpdateConnected(true)
            setEditableSkill(undefined)
            updateProgress()
            setSuccessNotificationPopup()
          }
        } catch (err) {
          setBackendError(err as IError)
        }
      }
    }
    setDeleteConnectedSkillConfirm(false)
  }

  const tableItems = useMemo(() => {
    return orderBy(
      items,
      ['level', item => (item.Skill ? chooseDBTranslation(i18n, item.Skill).name : null)],
      ['desc', 'asc']
    ).filter((obj, index, self) => index === self.findIndex(t => t.skillId === obj.skillId))
  }, [i18n, items])

  const openEditRow = useCallback((skill: IPersonSkillOrIndustryOrRole) => {
    setAddNew(false)
    setEditableSkill(skill)
  }, [])

  const columns = React.useMemo<ColumnDef<IPersonSkillOrIndustryOrRole>[]>(() => {
    const setNewItemValue = (newValue: number | string, field: string) => {
      if (field === 'lastUsed' || field === 'experienceMonths') {
        if (editableSkill) setEditableSkill({ ...editableSkill, [field]: toPositiveInteger(Number(newValue)) })
      } else {
        if (editableSkill) setEditableSkill({ ...editableSkill, [field]: newValue })
      }
    }

    const unspecified = () => <CardContentText type="error">{t(`profile.${type}.unspecified`)}</CardContentText>

    return [
      {
        id: 'name',
        header: t(`profile.${type}.card.nameColumn`),
        accessorFn: row => chooseDBTranslation(i18n, row.Skill).name,
        cell: ({ row }) => {
          return (
            <SkillsItem
              skill={row.original}
              editable={editable}
              showTooltip={true}
              key={row.original.id}
              showLastUsed={true}
              type={type}
            />
          )
        },
      },
      {
        id: 'level',
        header: t(`profile.${type}.level`),
        accessorFn: row => row.level,
        cell: ({ row }) => {
          return <SkillLevelChip level={row.original.level ?? 0} type="skill" />
        },
      },
      {
        id: 'interestLevel',
        header: t(`profile.${type}.interest`),
        accessorFn: row => row.interestLevel,
        cell: ({ row }) => {
          const thumbs: React.ReactNode[] = Array.from(Array(row.original.interestLevel), (_x, i) => (
            <ThumbUpIcon key={i} fontSize="small" style={{ padding: 1 }} color="secondary" />
          ))

          return row.original.interestLevel ? thumbs : unspecified()
        },
      },
      {
        id: 'experienceMonths',
        header: () => (
          <>
            {t(`profile.${type}.experience`)}
            <AutoCalculatedInfo text={t(`profile.${type}.autoCalculatedInfo`)} />
          </>
        ),
        accessorFn: row => row.experienceMonths,
        cell: ({ row }) => {
          return row.original.experienceMonths ? (
            getSkillExperienceLabel(row.original.experienceMonths)
          ) : (
            <CardContentText type="error">{t(`profile.${type}.unspecified`)}</CardContentText>
          )
        },
      },
      {
        id: 'lastUsed',
        header: () => (
          <>
            {t(`profile.${type}.lastUsed`)}
            <AutoCalculatedInfo text={t(`profile.${type}.autoCalculatedInfo`)} />
          </>
        ),
        accessorFn: row => row.lastUsed,
        cell: ({ row }) => {
          if (editable && editableSkill && row.original.id === editableSkill.id && !addNew) {
            return (
              <TableInputField
                value={editableSkill?.lastUsed ?? 2022}
                onChange={newValue => setNewItemValue(parseInt(newValue), 'lastUsed')}
                type="number"
                helperText={t(`profile.${type}.year`)}
                disabled={!editableSkill?.skillId}
                error={errors['lastUsed']}
              />
            )
          }
          return row.original.lastUsed
        },
      },
      {
        id: 'controls',
        header: t('admin.skills.actions'),
        cell: ({ row }) => {
          if (!editable || !row.original.id || !row.original.skillId) {
            return ''
          }

          return (
            <IconButton
              aria-label={t('edit')}
              size="small"
              onClick={event => {
                event.stopPropagation()
                openEditRow(row.original)
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )
        },
      },
    ]
  }, [t, type, i18n, editable, editableSkill, addNew, errors, openEditRow])

  const [{ pageIndex: defaultPageIndex, pageSize: defaultPageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  const pagination = useMemo(
    () => ({
      pageIndex: page ?? defaultPageIndex,
      pageSize: count ?? defaultPageSize,
    }),
    [defaultPageIndex, defaultPageSize, count, page]
  )

  const table = useReactTable({
    data: tableItems,
    columns: columns,
    initialState: {
      pagination: {
        pageIndex: page ?? 0,
        pageSize: count ?? 10,
      },
    },
    state: {
      sorting: sorting,
      pagination,
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableSorting: true,
    autoResetPageIndex: false,
  })

  useEffect(() => {
    const handleWarningItemClick = (event: Event) => {
      const { section, itemId } = (event as CustomEvent<{ section: string; itemId: number | string }>).detail
      if (section !== getType(type)) return

      const itemIndex = tableItems.findIndex(item => String(item.id) === String(itemId))
      if (itemIndex < 0) return

      const targetPage = Math.floor(itemIndex / (count ?? defaultPageSize))
      if (targetPage === table.getState().pagination.pageIndex) return

      pendingWarningItemId.current = itemId
      scrollAfterPageChange.current = true
      table.setPageIndex(targetPage)
      setPage(targetPage)
    }

    window.addEventListener('profile-warning-item-click', handleWarningItemClick)
    return () => window.removeEventListener('profile-warning-item-click', handleWarningItemClick)
  }, [count, defaultPageSize, setPage, table, tableItems, type])

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  const getType = (type: string) => {
    switch (type) {
      case 'skill':
        return 'skills'
      case 'industry':
        return 'industries'
      case 'role':
        return 'roles'
      default:
        return ''
    }
  }

  return (
    <Grid ref={tableRef} sx={{ scrollMarginTop: '160px' }}>
      {tableItems && (
        <>
          {editable && !addNew && (
            <AddToArrayButton
              icon={<AddIcon color="primary" fontSize="large" />}
              tooltip={t('add')}
              maxWidth={true}
              clickAction={() => {
                setEditableSkill(getSkillsInitialData(cvId) as unknown as IPersonSkillOrIndustryOrRole)
                setAddNew(true)
              }}
            />
          )}
          <Table
            size="small"
            sx={{
              [`& .${tableCellClasses.root}`]: {
                borderBottom: 'none',
              },
            }}
          >
            <TableHead
              sx={{ borderBottom: '1px solid #e0e0e0', backgroundColor: '#FFFFFF', position: 'sticky', top: 80 }}
            >
              {table.getHeaderGroups().map(headerGroup => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <TableCell
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{
                        maxWidth: header.getSize(),
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          {...{
                            style: header.column.getCanSort()
                              ? { cursor: 'pointer', fontWeight: 'bold' }
                              : { fontWeight: 'bold' },
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: <TableSortLabel active={true} direction="asc" />,
                            desc: <TableSortLabel active={true} direction="desc" />,
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {editable && editableSkill && !editableSkill.id && addNew && (
                <AdditionRow
                  type={type}
                  editableSkill={editableSkill}
                  getDisabledOptions={getDisabledOptions}
                  errors={errors}
                  setErrors={setErrors}
                  onSubmit={onSubmit}
                  setEditableSkill={setEditableSkill}
                  setAddNew={setAddNew}
                  schema={schema}
                />
              )}
              {table
                .getRowModel()
                .rows.reduce((accumulator: Row<IPersonSkillOrIndustryOrRole>[], row) => {
                  const exists = accumulator.find(item => item.original.id === row.original.id)
                  if (!exists) {
                    accumulator = accumulator.concat(row)
                  }
                  return accumulator
                }, [])
                .map(row => {
                  if (editableSkill && row.original.id === editableSkill.id && !addNew) {
                    // Edit row
                    return (
                      <EditRow
                        key={row.id}
                        editableSkill={editableSkill}
                        editable={editable}
                        type={type}
                        onSubmit={onSubmit}
                        errors={errors}
                        setErrors={setErrors}
                        setDeleteConnectedSkillConfirm={setDeleteConnectedSkillConfirm}
                        setEditableSkill={setEditableSkill}
                        schema={schema}
                      />
                    )
                  }

                  if (row.original.id && row.original.skillId) {
                    // No action row
                    return (
                      <TableRow
                        id={`profile-item-${getType(type)}-${row.original.id}`}
                        tabIndex={-1}
                        sx={{
                          '&:nth-of-type(odd)': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                        key={row.id}
                        onClick={editable ? () => openEditRow(row.original) : undefined}
                      >
                        {row.getVisibleCells().map(cell => (
                          <TableCell key={cell.id} sx={{ cursor: 'pointer' }}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    )
                  }
                })}
            </TableBody>
            <TableFooter sx={{ position: 'sticky', bottom: 0, insetBlockEnd: 0 }}>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  count={table.getPrePaginationRowModel().rows.length}
                  rowsPerPage={table.getState().pagination.pageSize}
                  page={table.getState().pagination.pageIndex}
                  onPageChange={(_, page) => {
                    scrollAfterPageChange.current = true
                    table.setPageIndex(page)
                    setPage(page)
                  }}
                  onRowsPerPageChange={e => {
                    const size = e.target.value ? Number(e.target.value) : 10
                    scrollAfterPageChange.current = true
                    table.setPageSize(size)
                    setCount(size)
                  }}
                  labelRowsPerPage={t('custom-table.labelRowsSelect')}
                  labelDisplayedRows={({ from, to, count }) =>
                    t('custom-table.labelDisplayedRows', { from: from, to: to, count: count })
                  }
                  ActionsComponent={TablePaginationActions}
                  sx={{
                    [`& .MuiTablePagination-spacer`]: {
                      display: 'none',
                      order: 9,
                    },
                    [`& .MuiTablePagination-displayedRows`]: {
                      order: 4,
                    },
                    [`& .MuiTablePagination-selectLabel`]: {
                      ml: 1,
                      order: 7,
                    },
                  }}
                  slotProps={{
                    select: {
                      inputProps: { 'aria-label': t('custom-table.labelRowsSelect') },
                      native: true,
                      sx: { order: 8 },
                    },
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </>
      )}
      {editableSkill && deleteConnectedSkillConfirm && (
        <DeleteConnectedSkillConfirm
          open={deleteConnectedSkillConfirm}
          confirm={deleteItem}
          onClose={() => setDeleteConnectedSkillConfirm(false)}
          item={editableSkill}
          type={type}
        />
      )}
    </Grid>
  )
}

export default SkillsTable
